import React, { useState, useEffect } from 'react';
import { sendEmailVerification } from 'firebase/auth';
import * as LucideIcons from 'lucide-react';

const EmailVerificationScreen = ({ user, onLogout }) => {
    const [isResending, setIsResending] = useState(false);
    const [resendMessage, setResendMessage] = useState('');
    const [cooldown, setCooldown] = useState(0);

    // Polling for verification status
    useEffect(() => {
        const interval = setInterval(async () => {
            try {
                await user.reload();
                if (user.emailVerified) {
                    // Force a reload of the page to pick up the new state cleanly
                    window.location.reload();
                }
            } catch (error) {
                console.error("Error checking verification status:", error);
            }
        }, 3000); // Check every 3 seconds

        return () => clearInterval(interval);
    }, [user]);

    // Resend Cooldown Timer
    useEffect(() => {
        if (cooldown > 0) {
            const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [cooldown]);

    const handleResend = async () => {
        if (cooldown > 0) return;
        
        setIsResending(true);
        setResendMessage('');
        
        try {
            await sendEmailVerification(user);
            setResendMessage('Verification email sent! Please check your inbox (and spam folder).');
            setCooldown(60); // 60 second cooldown
        } catch (error) {
            console.error("Error sending verification email:", error);
            if (error.code === 'auth/too-many-requests') {
                setResendMessage('Too many requests. Please wait a bit before trying again.');
                setCooldown(60);
            } else {
                setResendMessage('Failed to send email. Please try again later.');
            }
        } finally {
            setIsResending(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center p-4 text-center font-lora relative overflow-hidden">
            {/* Background Effects */}
            <div className="absolute inset-0 z-0 opacity-30 pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-purple-900 rounded-full blur-[100px] animate-pulse"></div>
                <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-blue-900 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '2s' }}></div>
            </div>

            <div className="relative z-10 max-w-md w-full bg-gray-800/80 backdrop-blur-lg border border-gray-700 rounded-2xl shadow-2xl p-8 transform transition-all hover:scale-[1.01]">
                <div className="mb-6 flex justify-center">
                    <div className="w-20 h-20 bg-purple-500/20 rounded-full flex items-center justify-center relative">
                        <div className="absolute inset-0 bg-purple-500/20 rounded-full animate-ping"></div>
                        <LucideIcons.Mail className="w-10 h-10 text-purple-400" />
                    </div>
                </div>

                <h1 className="text-3xl font-bold text-white mb-2 font-playfair">Verify Your Echo</h1>
                <p className="text-blue-200 mb-6 text-lg">
                    We've sent a verification link to <span className="font-semibold text-white">{user.email}</span>.
                </p>

                <div className="bg-gray-900/50 rounded-lg p-4 mb-8 border border-gray-700/50">
                    <div className="flex items-center justify-center gap-3 text-sm text-gray-400 mb-2">
                        <LucideIcons.Loader2 className="w-4 h-4 animate-spin" />
                        <span>Waiting for verification...</span>
                    </div>
                    <p className="text-xs text-gray-500">
                        Click the link in your email to automatically proceed.
                    </p>
                </div>

                <div className="space-y-4">
                    <button
                        onClick={handleResend}
                        disabled={isResending || cooldown > 0}
                        className={`w-full py-3 px-6 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2
                            ${isResending || cooldown > 0 
                                ? 'bg-gray-700 text-gray-400 cursor-not-allowed' 
                                : 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white shadow-lg hover:shadow-purple-500/25'
                            }`}
                    >
                        {isResending ? (
                            <LucideIcons.Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                            <LucideIcons.Send className="w-5 h-5" />
                        )}
                        {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend Verification Email'}
                    </button>

                    {resendMessage && (
                        <p className={`text-sm ${resendMessage.includes('sent') ? 'text-green-400' : 'text-red-400'} animate-fade-in`}>
                            {resendMessage}
                        </p>
                    )}

                    <button
                        onClick={onLogout}
                        className="w-full py-2 text-gray-400 hover:text-white text-sm transition-colors flex items-center justify-center gap-2"
                    >
                        <LucideIcons.LogOut className="w-4 h-4" />
                        Sign Out / Change Email
                    </button>
                </div>
            </div>

            <div className="mt-8 text-gray-500 text-sm max-w-sm">
                <p>Did you check your Spam folder? The harmony sometimes gets lost in the void.</p>
            </div>
        </div>
    );
};

export default EmailVerificationScreen;
